/* Region Context - cache bust */
import React, { createContext, useContext, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Region,
  RegionConfig,
  SiteRegion,
  SITE_REGIONS,
  SITE_REGION_CODES,
  CONTENT_REGION,
  DEFAULT_REGION,
} from './types';
import { commonTranslations } from './translations/common';
import { serviceTranslations, getServiceLinks } from './translations/services';
import { localizeSlug, withTrailingSlash } from '@/router/routeManifest';

interface RegionContextValue {
  /** Translation bucket (`at` reads `de`). Use it to look up copy. */
  region: Region;
  /** URL prefix of the current page (`de`, `ch`, `at` or `en`). */
  siteRegion: SiteRegion;
  config: RegionConfig;
  t: typeof commonTranslations['de'];
  services: typeof serviceTranslations['de'];
  serviceLinks: ReturnType<typeof getServiceLinks>;
  switchRegion: (newRegion: SiteRegion) => void;
  getLocalizedPath: (path: string) => string;
  isGermanSpeaking: boolean;
}

const RegionContext = createContext<RegionContextValue | undefined>(undefined);

export const RegionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { lang } = useParams<{ lang: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const siteRegion: SiteRegion = (SITE_REGION_CODES as readonly string[]).includes(lang ?? '')
    ? (lang as SiteRegion)
    : DEFAULT_REGION;
  const region: Region = CONTENT_REGION[siteRegion];
  const config = SITE_REGIONS[siteRegion];
  const t = commonTranslations[region];
  const services = serviceTranslations[region];
  const serviceLinks = getServiceLinks(region);
  const isGermanSpeaking = region !== 'en';

  const switchRegion = (newRegion: SiteRegion) => {
    const currentPath = location.pathname;
    // Remove current region prefix and add new one
    const pathWithoutRegion = currentPath.replace(/^\/(de|ch|at|en)(?=\/|$)/, '');
    const newPath = `/${newRegion}${pathWithoutRegion || ''}`;
    navigate(newPath);
  };

  const getLocalizedPath = (path: string): string => {
    // Strip an existing region prefix, translate the slug, add the current prefix
    const withoutRegion = path.replace(/^\/(de|ch|at|en)(?=\/|$|[?#])/, '');
    const localized = localizeSlug(siteRegion, withoutRegion.startsWith('/') ? withoutRegion : `/${withoutRegion}`);
    return withTrailingSlash(`/${siteRegion}${localized}`);
  };

  const value = useMemo(
    () => ({
      region,
      siteRegion,
      config,
      t,
      services,
      serviceLinks,
      switchRegion,
      getLocalizedPath,
      isGermanSpeaking,
    }),
    [region, location.pathname]
  );

  return <RegionContext.Provider value={value}>{children}</RegionContext.Provider>;
};

export const useRegion = (): RegionContextValue => {
  const context = useContext(RegionContext);
  if (!context) {
    throw new Error('useRegion must be used within a RegionProvider');
  }
  return context;
};
